import { addAction, recordException } from '@/lib/tracing/otel-browser-logger';
import posthog from 'posthog-js';

/**
 * Attributes is a map from string to attribute values.
 *
 * Note: only the own enumerable keys are counted as valid attribute keys.
 */
export interface Attributes {
  [attributeKey: string]: AttributeValue | undefined;
}

/**
 * Attribute values may be any non-nullish primitive value except an object.
 *
 * null or undefined attribute values are invalid and will result in undefined behavior.
 */
export declare type AttributeValue =
  | string
  | number
  | boolean
  | Array<null | undefined | string>
  | Array<null | undefined | number>
  | Array<null | undefined | boolean>;

/**
 * Browser logger for distributed tracing events.
 *
 * Sends events to SigNoz (via OpenTelemetry) for correlation with traces.
 * These events will NOT be logged to the browser console (silent logging).
 *
 * @returns Logger object with log and captureException methods
 */
export const BrowserLogger = () => {
  return {
    /**
     * Log an action/event for distributed tracing correlation.
     * Equivalent to the old HyperDX.addAction().
     */
    log: (name: string, attributes?: Attributes) => {
      try {
        // Convert Attributes to Record<string, unknown> for addAction
        const convertedAttributes = attributes
          ? Object.fromEntries(
              Object.entries(attributes).filter(([, v]) => v !== undefined)
            )
          : undefined;
        addAction(name, convertedAttributes);
      } catch (error) {
        console.error('Failed to log event:', error);
      }
    },

    /**
     * Capture an exception for error tracking.
     * Sends to both SigNoz (OpenTelemetry) and PostHog.
     */
    captureException: (error: unknown, attributes?: Attributes) => {
      // Send to SigNoz via OpenTelemetry
      try {
        const errorObj = error instanceof Error ? error : new Error(String(error));
        const convertedAttributes = attributes
          ? Object.fromEntries(
              Object.entries(attributes).filter(([, v]) => v !== undefined)
            )
          : undefined;
        recordException(errorObj, convertedAttributes);
      } catch (err) {
        console.error('OTEL: Failed to capture exception:', err);
      }

      // Also send to PostHog
      try {
        posthog.captureException(error, attributes);
      } catch (err) {
        console.error('PH: Failed to capture exception:', err);
      }
    },
  };
};

// Singleton instance
const browserLogger = BrowserLogger();

export { browserLogger };
